import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, 
  ArrowRight, 
  Loader2, 
  Shield,
  Star,
  Zap,
  BarChart3,
  HeadphonesIcon,
  Crown,
  Calendar
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChoosePlanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerId, setCustomerId] = useState('');
  
  useEffect(() => {
    // Get customer info from URL params or session
    const email = searchParams.get('email');
    const id = searchParams.get('customer_id');
    if (email) setCustomerEmail(email);
    if (id) setCustomerId(id);
  }, [searchParams]);

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        package_id: planId,
        origin_url: window.location.origin,
        customer_email: customerEmail,
        metadata: { customer_id: customerId }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('There was an error processing your request. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="font-bold text-xl">Alaska AI Services</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Setup Complete! 🎉
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Your AI automation is ready. Choose your ongoing maintenance plan to keep everything running smoothly.
          </p>
        </motion.div>

        {/* Plan Selection */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Standard Plan */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900 border border-slate-700 rounded-2xl p-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-6 h-6 text-slate-400" />
              <h2 className="text-xl font-bold">Standard</h2>
            </div>
            <p className="text-slate-400 text-sm mb-6">Essential maintenance for your AI</p>
            
            <div className="mb-6">
              <span className="text-4xl font-bold">$47.98</span>
              <span className="text-slate-400 ml-2">/month</span>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                Standard client dashboard
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                Basic support (email)
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                System monitoring
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                Monthly reports
              </li>
              <li className="flex items-center gap-3 text-slate-500">
                <span className="w-5 h-5 flex-shrink-0 text-center">—</span>
                <span className="line-through">AI optimization</span>
              </li>
              <li className="flex items-center gap-3 text-slate-500">
                <span className="w-5 h-5 flex-shrink-0 text-center">—</span>
                <span className="line-through">Priority support</span>
              </li>
              <li className="flex items-center gap-3 text-slate-500">
                <span className="w-5 h-5 flex-shrink-0 text-center">—</span>
                <span className="line-through">Premium dashboard</span>
              </li>
            </ul>

            <Button
              onClick={() => handleSelectPlan('standard_maintenance')}
              disabled={loading === 'standard_maintenance'}
              variant="outline"
              className="w-full border-slate-600 text-white hover:bg-slate-800 py-5 font-semibold"
              data-testid="select-standard"
            >
              {loading === 'standard_maintenance' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Select Standard <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </motion.div>

          {/* Premium Plan */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900 border-2 border-yellow-500/50 rounded-2xl p-8 relative"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full text-sm font-semibold text-black">
              Recommended
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold">Premium</h2>
            </div>
            <p className="text-slate-400 text-sm mb-6">Full-service with all the bells & whistles</p>
            
            <div className="mb-6">
              <span className="text-4xl font-bold">$397</span>
              <span className="text-slate-400 ml-2">/month</span>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-slate-300">
                <Zap className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">AI optimization & tuning</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <BarChart3 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">Weekly analytics reports</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <HeadphonesIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">Priority support (phone & email)</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Calendar className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">Feature updates & upgrades</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">Premium dashboard</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-yellow-400 font-medium">Premium contractor listing</span>
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                Everything in Standard
              </li>
            </ul>

            <Button
              onClick={() => handleSelectPlan('premium_maintenance')}
              disabled={loading === 'premium_maintenance'}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black py-5 font-semibold"
              data-testid="select-premium"
            >
              {loading === 'premium_maintenance' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Select Premium <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            <p className="text-center text-xs text-slate-500 mt-4">
              Best value for serious contractors
            </p>
          </motion.div>
        </div>

        {/* Skip Option */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/client/login')}
            className="text-slate-500 hover:text-slate-400 text-sm underline"
          >
            Skip for now and decide later
          </button>
          <p className="text-slate-600 text-xs mt-2">
            You can always upgrade from your dashboard
          </p>
        </div>
      </main>
    </div>
  );
}
